import React, { useState } from 'react';
import { useAppSelector } from '../store/hooks';
import { parseOrderEmails } from '../utils/emailParser';
import { EmailData } from '../utils/emailFetcher';
import ProcessEmailOrdersModal from '../features/orders/ProcessEmailOrdersModal';
import ReviewQueueModal from '../features/orders/ReviewQueueModal';

const EmailOrderProcessor: React.FC = () => {
  const [testEmailText, setTestEmailText] = useState('');
  const [parsedEmails, setParsedEmails] = useState<any[]>([]);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showReviewQueue, setShowReviewQueue] = useState(false);

  const fillingsRecipes = useAppSelector(state => state.recipes.fillings);
  const componentsRecipes = useAppSelector(state => state.recipes.components);
  const reviewQueueItems = useAppSelector(state => state.reviewQueue.items);

  // Combine all recipes
  const allRecipes = [
    ...fillingsRecipes.map(r => ({ id: r.id, name: r.name })),
    ...componentsRecipes.map(r => ({ id: r.id, name: r.name }))
  ];

  const handleLoadSample = () => {
    const sampleEmail = `Name

John Smith

Email

john.smith@example.com

Phone

416-555-0123

Address

123 Main Street
Toronto, ON M5V 3A8

Your Order

Chocolate Chip Cookies x3
Oatmeal Raisin Cookies - 2 dozen
Peanut Butter Cookies (1)

Thank you for your order!`;

    setTestEmailText(sampleEmail);
  };

  const handleLoadIncompleteSample = () => {
    const sampleEmail = `Name

Jane Doe

Email

jane.doe@example.com

Your Order

Sugar Cookies x2

Thank you!`;

    setTestEmailText(sampleEmail);
  };

  const handleParse = () => {
    if (!testEmailText.trim()) {
      alert('Please enter email text or load a sample');
      return;
    }

    // Create a mock EmailData object
    const mockEmailData: EmailData = {
      subject: 'New Order',
      from: 'test@example.com',
      replyTo: 'test@example.com',
      body: testEmailText,
      date: new Date(),
      receivedAt: new Date().toISOString()
    };

    // Parse the email
    const parsed = parseOrderEmails([mockEmailData], allRecipes);
    setParsedEmails(parsed);

    // Show processing modal
    setShowProcessModal(true);
  };

  const handleClear = () => {
    setTestEmailText('');
    setParsedEmails([]);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '2rem', color: '#333' }}>Email Order Processor</h1>
        <p style={{ margin: 0, fontSize: '0.95rem', color: '#666' }}>
          Test the auto-creation flow with email validation and duplicate detection
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px',
          marginBottom: '24px'
        }}
      >
        {/* Input Section */}
        <div>
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '20px'
            }}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: '#333' }}>Email Input</h2>

            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  color: '#555'
                }}
              >
                Email Body Text
              </label>
              <textarea
                value={testEmailText}
                onChange={e => setTestEmailText(e.target.value)}
                placeholder="Paste email body text here..."
                style={{
                  width: '100%',
                  height: '300px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleLoadSample}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                Load Complete Sample
              </button>
              <button
                onClick={handleLoadIncompleteSample}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                Load Incomplete Sample
              </button>
              <button
                onClick={handleClear}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <button
            onClick={handleParse}
            disabled={!testEmailText.trim()}
            style={{
              marginTop: '16px',
              width: '100%',
              padding: '16px',
              backgroundColor: testEmailText.trim() ? '#4CAF50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: testEmailText.trim() ? 'pointer' : 'not-allowed',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Parse & Process Email
          </button>
        </div>

        {/* Info Section */}
        <div>
          <div
            style={{
              backgroundColor: '#F5F5F5',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '20px'
            }}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '1.2rem', color: '#333' }}>How It Works</h2>

            <div style={{ fontSize: '0.9rem', color: '#555', lineHeight: '1.6' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#333' }}>Auto-Creation Flow</h3>
              <ol style={{ margin: '0 0 16px 0', paddingLeft: '24px' }}>
                <li>Email is parsed to extract customer info and order items</li>
                <li>
                  <strong>Validation</strong> checks for:
                  <ul style={{ marginTop: '4px', marginBottom: '8px' }}>
                    <li>Customer name</li>
                    <li>Valid email address</li>
                    <li>Valid phone number</li>
                    <li>Complete address (street, city, province, postal code)</li>
                    <li>At least one order item</li>
                  </ul>
                </li>
                <li>
                  <strong>Duplicate detection</strong> searches for orders from same email in last 7 days
                </li>
                <li>
                  If validation passes and no duplicate:
                  <ul style={{ marginTop: '4px', marginBottom: '8px' }}>
                    <li>Auto-creates or links to existing client</li>
                    <li>Creates order in "Pending" status</li>
                  </ul>
                </li>
                <li>
                  If validation fails or duplicate found:
                  <ul style={{ marginTop: '4px' }}>
                    <li>Routes to review queue with flag reason</li>
                    <li>Awaits manual review and approval</li>
                  </ul>
                </li>
              </ol>

              <h3 style={{ margin: '16px 0 8px 0', fontSize: '1rem', color: '#333' }}>Review Queue</h3>
              <p style={{ margin: '0 0 8px 0' }}>
                Items in review queue can be edited and approved. Upon approval, they create orders and clients the same
                way as auto-created orders.
              </p>
            </div>
          </div>

          {reviewQueueItems.length > 0 && (
            <div
              style={{
                marginTop: '16px',
                backgroundColor: '#FFF3E0',
                border: '1px solid #FFB74D',
                borderRadius: '8px',
                padding: '16px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '1rem', color: '#E65100' }}>Review Queue</h3>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                    {reviewQueueItems.length} item{reviewQueueItems.length !== 1 ? 's' : ''} pending review
                  </p>
                </div>
                <button
                  onClick={() => setShowReviewQueue(true)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#FF9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Open Queue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ProcessEmailOrdersModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        parsedEmails={parsedEmails}
        availableRecipes={allRecipes}
      />

      <ReviewQueueModal
        isOpen={showReviewQueue}
        onClose={() => setShowReviewQueue(false)}
        availableRecipes={allRecipes}
      />
    </div>
  );
};

export default EmailOrderProcessor;
